import type { PaymentAction, PaymentMethodsResponse } from '@adyen/adyen-web';
import type { AxiosRequestConfig } from 'axios';

import { company } from '~/company/Company';

import type { OrderItemBundle } from './Catalog';
import type { Customer } from './Customer';
import { ENDPOINT } from './constants';
import type { ETAPaymentMethod } from './ETA';
import type { ETADeliveryMethodType } from './ETADeliveryMethodType';
import type { IPromocode } from './Promocodes';
import { RequestAPI } from './Requests';
import { getLangHeaders } from './utils';

export type NewOrderCartItem = Omit<
  OrderCartItem,
  'id' | 'order_id' | 'created_at' | 'updated_at' | 'status' | 'actual_quantity'
>;

export type OrderCartItemStatus =
  | 'ordered'
  | 'reserved'
  | 'reserved_partially'
  | 'picked_partially'
  | 'missing'
  | 'picked'
  | 'out_of_stock';

export type OrderPublicStatus =
  | 'accepted'
  | 'picking'
  | 'ready_to_ship'
  | 'ready_for_pickup'
  | 'in_delivery'
  | 'delivered'
  | 'failed'
  | 'cancelled';

export type OrderStatus =
  | 'created'
  | 'reserved'
  | 'confirmed'
  | 'delivery_created'
  | 'wh_created'
  | 'picking'
  | 'ready_to_ship'
  | 'in_delivery'
  | 'delivered'
  | 'delivered_partially'
  | 'finished'
  | 'cancelled'
  | 'delivery_failed';

export enum PaymentSystem {
  stripe = 'stripe',
  myfatoorah = 'myfatoorah',
  midtrans = 'midtrans',
  amazon = 'amazon',
  adyen = 'adyen',
  verifone = 'verifone',
  pvit = 'pvit',
  cash = 'cash',
  octo = 'octo',
}

export interface PaymentSystemItem {
  system: PaymentSystem;
  methods: ETAPaymentMethod[];
}

export interface OrderCartItem {
  id: string;
  order_id: string;
  created_at: string;
  updated_at: string;
  status: OrderCartItemStatus;
  sku: string;
  requested_quantity: number;
  actual_quantity: number;
  picked_quantity: number | null;
  weight: number;
  base_price: number;
  discount_amount: number;
  paid_price: number;
  product_id: string;
  name: string;
  image: string;
  price_per_unit: string;
  promocode_discount_amount: number;
  paid_bonuses: number;
  promo_required_quantity?: number | null;
  promo_quantity_discount_price?: number | null;
  bundle_id?: string | null;
  is_gift?: boolean;
}

export interface OrderAddress {
  id: string;
  order_id: string;
  city: string;
  country_id: string;
  region: string;
  address_1: string | null;
  address_2: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  instructions: string[] | null;
  comment: string | null;
  zip: string;
  customer_address_id: string;
  building: string;
  apartment: string;
  floor: string;
  type: string;
  place_id: string;
}

interface OrderSla {
  start_after: string;
  ship_by: string;
  deliver_by_min: string;
  deliver_by_max: string;
}

interface Recipient {
  id: string;
  order_id: string;
  full_name: string;
  email: string;
  phone: string;
}

interface OrderDelivery {
  tracking?: {
    pickup?: string | null;
    dropoff?: string | null;
  };
}

interface OrderPicking {
  id: string;
  picker_id: null;
  oms_order_id: string;
  transfer_id: number;
  transfer_type: string;
  odoo_request_id: string;
  warehouse_code: string;
  sla_start_after: string;
  sla_ship_by: string;
  startedAt: null;
  completedAt: null;
  status: string;
  createdAt: string;
  updatedAt: string;
  picker: null;
}

interface OrderPayment {
  order_id: string;
  status: string;
  payment_method: ETAPaymentMethod;
  payment_system: PaymentSystem;
  payment_metadata: {
    qrCode: string;
  };
  created_at: string;
}

export interface OrderShort {
  id: string;
  status: OrderStatus;
  created_at: string;
  updated_at: string;
  customer_id: string;
  delivery_id: null;
  payment_id: null;
  order_id: string;
  is_imported: boolean;
  warehouse_code: string;
  currency: string;
  warehouse_id: string;
  has_feedback: boolean;
  delivery_state: null;
  items: OrderCartItem[];
  receipt_url: string | null;
  address: OrderAddress | null;
  recipient: Recipient | null;
  public_status: OrderPublicStatus;
  sla: OrderSla;
  promocode: string | null;
  base_total: number | null;
  promocode_discount: number | null;
  discount_total: number | null;
  paid_total: number | null;
  actual_paid_loyalty_points: number | null;
  delivery_price: number | null;
  is_delivery_free: boolean | null;
  save_card: boolean;
  service_fee: number;
  cancel_reason: 'CANCEL_BY_USER' | null;
  delivery_method: ETADeliveryMethodType;
  distance_to_warehouse: number;
  warehouse_location_latitude: number;
  warehouse_location_longitude: number;
  received_bonuses: number;
  payment_method: ETAPaymentMethod;
  payment: OrderPayment;
  tax_percent?: number;
  tax_amount?: number;
  bundles?: OrderItemBundle[];
  itemsCount: {
    actual: number;
    requested: number;
  };
  actual_paid_price: number;
  actual_paid_price_money: number;
  actual_tax_amount: number;
  delivery_tracking_url?: string;
  delivery_zone_id: string | null;
  delivery_zone_name: string | null;
  /** When the order will be cancelled (only when status === 'created' or 'reserved') */
  cancel_at?: string;
}

export interface OrderWithPromocode extends OrderShort {
  promocode_data: IPromocode;
}

export interface Order extends OrderShort {
  delivery: OrderDelivery;
  picking: OrderPicking;
  customer: Customer[];
  payment: OrderPayment;
  finishedMoreThanDayAgo: boolean;
  warehouse_address: string;
  order_delivery?: { warehouse_address?: string };
}
export interface AwsPayment {
  id: string;
  public_identifier: string;
  payment_system_id: string;
  payment_system_access: string;
  customer_id: string;
  company_id: string;
  created_at: string;
  is_default: boolean;
}
export interface Payment {
  card: {
    brand: string;
    checks: {
      address_line1_check: 'pass' | null;
      address_postal_code_check: 'pass' | null;
      cvc_check: 'pass' | null;
    };
    country: string;
    exp_month: number;
    exp_year: number;
    fingerprint: string;
    last4: string;
  };
  id: string;
  paymentToken: string | null;
  metadata: {
    is_default: string;
  };
}

export interface VerifonePaymentCard {
  id: string;
  customer_id: string;
  company_id: string;
  provider: string;
  provider_token: string;
  card_brand: string;
  card_last_four: string;
  card_exp_month: number;
  card_exp_year: number;
  card_holder_name: string | null;
  card_type: string;
  token_expiry_date: string;
  stored_credential_reference: string;
  scheme_reference: string;
  token_scope: string;
  metadata: {
    id: string;
    arn: string;
    stan: string;
    amount: number;
    status: string;
    blocked: boolean;
    details: {
      auto_capture: boolean;
    };
    created_by: string;
    cvv_result: string;
    cvv_present: boolean;
    reason_code: string;
    card_details: {
      expiry_year: number;
      expiry_month: number;
      masked_card_number: string;
    };
    token_details: {
      bin: string;
      type: string;
      brand: string;
      variant: string;
      last_four: string;
      created_at: string;
      updated_at: string;
      expiry_year: number;
      issuer_name: string;
      reuse_token: string;
      token_scope: string;
      expiry_month: number;
      token_status: string;
      currency_code: string;
      issuer_country: string;
      token_expiry_date: string;
    };
    balance_amount: number;
    additional_data: {
      initiator_trace_id: string;
    };
    payment_product: string;
    reversal_status: string;
    scheme_reference: string;
    stored_credential: {
      reference: string;
      processing_model: string;
      stored_credential_type: string;
    };
    merchant_reference: string;
    shopper_interaction: string;
    threed_authentication: {
      cavv: string;
      eci_flag: string;
      enrolled: string;
      pares_status: string;
      threeds_version: string;
      ds_transaction_id: string;
    };
    promo_financing_result: Record<string, unknown>;
    payment_provider_contract: string;
  };
  is_default: boolean;
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

export interface GiftMechanism {
  id: string;
  cms_id: number;
  name: string;
  minimum_order_amount: number;
  gift_items: GiftItem[];
}

export interface GiftItem {
  id: number;
  name: string;
  code: string;
  price?: number;
  preview_image: string;
  preview_image_thumb: string;
}

export interface FillFeedbackRequest {
  rating: number;
  delivery_rating: number;
  product_variety_rating: number;
  product_quality_rating: number;
  comment: string;
}

export interface NewOrderResponseOLD {
  data: {
    multi_order?: {
      idempotency_key: string;
      company_id: string;
      order_id: string;
      created_at: number;
      updated_at: number;
      id: string;
    };
    orders: Array<{
      order: {
        id: string;
        order_id: string;
        duration: {
          min: number;
          max: number;
        };
        seller: string;
        payment_method: string;
        company_id: string;
      };
      payment_intent: {
        client_secret?: string;
      };
      payment: {
        id: string;
        myFatoorahPayment: {
          invoiceId?: number;
          paymentURL?: string;
          error?: boolean;
        };
      };
      payed_delivery: {
        shipping_cost: number;
        cost_threshold: number;
      };
      errors?:
        | boolean
        | Array<{
            message?: string;
            payload?: {
              sku: string;
              warehouse_code: string;
            };
          }>;
    }>;
  };
}

export interface NewOrderResponse {
  data: {
    multi_order?: {
      idempotency_key: string;
      company_id: string;
      order_id: string;
      created_at: number;
      updated_at: number;
      id: string;
    };
    orders: Array<{
      order: {
        id: string;
        order_id: string;
        duration: {
          min: number;
          max: number;
        };
        seller: string;
        payment_method: string;
        company_id: string;
        cancellationDelay: number;
      };
      payment_intent: {
        client_secret?: string;
      };
      payment: {
        id: string;
        providerPaymentDetails?: {
          paymentSystem: string;
          providerPaymentId: string;
          paymentLink?: string;
          paymentSession: string;
          additionalData?: {
            action: PaymentAction;
            acs_url?: string;
            payload?: string;
            transaction_id?: string;
          };
        };
        midtransPayment?: {
          id: string;
          redirect_url: string;
          token: string;
        };
        myFatoorahPayment: {
          invoiceId?: number;
          paymentURL?: string;
          error?: boolean;
        };
      };
      payed_delivery: {
        shipping_cost: number;
        cost_threshold: number;
      };
      errors?:
        | boolean
        | Array<{
            message?: string;
            payload?: {
              sku: string;
              warehouse_code: string;
            };
          }>;
    }>;
  };
}

export interface OrdersResponse {
  orders: OrderShort[];
  total_count: number;
}

export interface OrdersWithPromocodesResponse {
  data: OrderWithPromocode[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
}

interface OrderResponse {
  order: Order;
}

export type Aws3DsStatus = 'waiting_3ds' | 'cancelled' | 'charged' | 'created';

export interface CheckPromocodeResponse {
  id: number;
  type: 'PERCENTAGE' | 'FIXED' | 'FREE_SHIPPING' | 'BUY_X_GET_Y';
  code: string;
  filters: Unnecessary<Record<string, unknown>>;
  value: number;
  applyToDiscountedItems: boolean;
  minimumPurchase: number;
  minimumQuantity: number;
  usageLimitCustomer: number;
  usageCurrent: number;
  usageLimitAll: number;
  dateRange: [string, string];
  orderNumberRange: [number, number];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  productDiscountQuantity: number;
  discountTargets: null;
}

interface GiftResponse {
  data?: GiftMechanism;
}

interface CheckPayedTipResponse {
  status?: 'created' | 'received';
  errors?: string[];
}

export type NewOrderCartItemBundle = {
  id: number;
  requested_quantity: number;
};

export type NewOrderCartItemOffer = {
  base_price: number;
  discount_amount: number;
  image: string;
  name: string;
  paid_bonuses: number;
  paid_price: number;
  price_per_unit: string;
  product_id: string;
  promocode_discount_amount: number;
  requested_quantity: number;
  sku: string;
  weight: number;
};

export type NewOrderDelivery = {
  base_total: number;
  delivery_price: number;
  discount_total: number;
  is_delivery_free: boolean;
  paid_bonuses: number;
  paid_total: number;
  promocode: string | null;
  promocode_discount: number;
  service_fee: number;
  should_use_loyalty_points: boolean;
  should_use_bonuses: boolean;
  tips_amount: number;
  items: NewOrderCartItemOffer[];
  bundles?: NewOrderCartItemBundle[];
  slot_delivery_details?: {
    current_date: number;
    schedule_slot_id: string;
  };
};

export type NewOrderContent = {
  client_source: string;
  currency: string;
  customer_id: string;
  delivery_method: string;
  delivery_provider?: string;
  delivery_mechanic?: string;
  warehouse_code: string;
  save_card: boolean;
  session_id?: string;
  deliveries: Array<NewOrderDelivery>;
  payment?: {
    pay_by_saved_card?: boolean;
    card_id?: Nullable<string>;
    payment_method: ETAPaymentMethod;
  };
};

interface OrderRequestInput {
  address: Omit<OrderAddress, 'id' | 'order_id'>;
  recipient: Omit<Recipient, 'id' | 'order_id'>;
  order: NewOrderContent;
}

interface CurrencyResponse {
  data: {
    currency: string;
    digits_after_coma: number;
  };
}

interface OrderPaymentDetails {
  payment_id: string;
  details: unknown;
}

export interface MyfatoorahInit {
  SessionId: string;
  CountryCode: string;
}

interface Verifone3dsData {
  Amount: number;
  AuthorizationPayload: string;
  CAVV: string;
  CurrencyCode: string;
  ECIFlag: string;
  PAResStatus: string;
  SignatureVerification: string;
  ThreeDSVersion: string;
}

interface Verifone3dsConfirmDto {
  details: {
    encryptedCard: string;
    threedAuthentication: Verifone3dsData;
  };
  payment_id: string;
}

interface OrderRequestsInterface {
  newOrder(data: OrderRequestInput, key: string): Promise<NewOrderResponse>;

  orderPaymentDetails(data: OrderPaymentDetails): Promise<void>;

  orderPaid(orderId: string): Promise<{ status: string }>;

  orderCancel(orderId: string): Promise<{ status: string }>;

  orderIdempotencyCancel(data: {
    idempotency_key: string;
  }): Promise<{ status: string }>;

  getOrders(data: {
    filter?: {
      customer_id?: string;
      status?: OrderStatus[];
    };
    options?: Record<string, any>;
  }): Promise<OrdersResponse>;

  getOrder(id: string): Promise<OrderResponse>;

  get3dsStatus(id: string): Promise<{
    '3ds_url': string;
    status: Aws3DsStatus;
  }>;

  verifone3ds(): Promise<{ jwt: string }>;

  verifone3dsConfirm(data: Verifone3dsConfirmDto): Promise<any>;

  getPayments(
    data: PaymentSystem[],
    deliveryZoneId?: string,
  ): Promise<Payment[] | AwsPayment[] | PaymentMethodsResponse>;

  createIntent(): Promise<Payment>;

  addPayment(data: { payment_method_id: string }): Promise<Payment>;

  deletePayment(data: {
    payment_method_id: string;
    deliveryZoneId?: string;
  }): Promise<Payment>;

  setDefaultPayment(data: {
    payment_method_id: string;
    deliveryZoneId?: string;
  }): Promise<Payment>;

  newFeedback(data: {
    rating: number;
    order_id: string;
  }): Promise<{ id: string }>;

  fillFeedback(feedbackId: string, data: FillFeedbackRequest): Promise<void>;

  checkPromocode(
    promocode: string,
    options?: AxiosRequestConfig,
  ): Promise<CheckPromocodeResponse>;

  getGifts(warehouseCode: string): Promise<GiftResponse>;

  payTip(
    orderId: string,
    data: {
      amount: number;
      save_card: boolean;
      currency: string;
      email: string;
    },
  ): Promise<{ client_secret: string }>;

  checkPayedTip(orderId: string): Promise<CheckPayedTipResponse>;

  getCurrency(): Promise<CurrencyResponse>;

  getPaymentSystems(deliveryZoneId?: string): Promise<PaymentSystemItem[]>;

  myfatoorahInit(): Promise<MyfatoorahInit>;

  getOrdersWithPromocodes(data: {
    page: number;
    limit: number;
  }): Promise<OrdersWithPromocodesResponse>;
}

export const OrderRequests: OrderRequestsInterface = {
  newOrder: (data, key) =>
    RequestAPI.post(ENDPOINT.orders.new, data, {
      headers: {
        'Idempotency-Key': key,
        ...getLangHeaders().headers,
      },
    }),

  orderPaid: (orderId) => RequestAPI.post(ENDPOINT.orders.paid + orderId),

  orderPaymentDetails: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.details, data),

  get3dsStatus: (orderId) =>
    RequestAPI.get(ENDPOINT.orders.paymentMethods.status3ds + orderId),

  verifone3ds: () =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.verifone3ds, {
      companyId: company.config.id,
    }),

  verifone3dsConfirm: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.verifone3dsConfirm, data),

  orderCancel: (orderId) =>
    RequestAPI.post(ENDPOINT.orders.cancelByUser + `/${orderId}`),

  orderIdempotencyCancel: (data) =>
    RequestAPI.post(ENDPOINT.orders.cancelByUser, data),

  getOrders: (data) =>
    RequestAPI.post(ENDPOINT.orders.status, data, getLangHeaders()),

  getOrder: (id) =>
    RequestAPI.get(ENDPOINT.orders.status + `/${id}`, {}, getLangHeaders()),

  getPayments: (data, deliveryZoneId) =>
    RequestAPI.get(ENDPOINT.orders.paymentMethods.list, {
      availablePaymentProviders: data,
      ...(deliveryZoneId && { deliveryZoneId }),
    }),

  addPayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.add, data),

  deletePayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.remove, data),

  createIntent: () =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.setupIntent),

  setDefaultPayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.setDefault, data),

  newFeedback: (data) => RequestAPI.post(ENDPOINT.orders.feedback.create, data),

  fillFeedback: (feedbackId, data) =>
    RequestAPI.post(ENDPOINT.orders.feedback.fill + feedbackId, data),

  checkPromocode: (promocode, options) =>
    RequestAPI.get(ENDPOINT.promocodes.get + promocode, undefined, options),

  getGifts: (warehouseCode) =>
    RequestAPI.get(ENDPOINT.gifts.getMechanisms + warehouseCode),

  payTip: (orderId, data) => RequestAPI.post(ENDPOINT.tips.pay + orderId, data),

  checkPayedTip: (orderId) => RequestAPI.get(ENDPOINT.tips.check + orderId),

  getCurrency: () => RequestAPI.get(ENDPOINT.orders.getCurrency),

  getPaymentSystems: (deliveryZoneId?: string) =>
    RequestAPI.get(ENDPOINT.payments.paymentSystems, {
      ...(deliveryZoneId && { deliveryZoneId }),
    }),

  myfatoorahInit: () => RequestAPI.post(ENDPOINT.payments.myfatoorahInitialize),

  getOrdersWithPromocodes: (data) =>
    RequestAPI.get(ENDPOINT.orders.getOrdersWithPromocodes, data),
};
